import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { LoginArea } from '@/components/auth/LoginArea';
import { toast } from '@/hooks/useToast';
import { AlertTriangle, Loader2 } from 'lucide-react';
import type { Merchant } from '@/lib/merchants';

const REPORT_TYPES = [
  { value: 'spam', label: 'Spam', description: 'Fake listing or advertising' },
  { value: 'impersonation', label: 'Impersonation', description: 'Pretending to be another business' },
  { value: 'illegal', label: 'Illegal', description: 'Illegal content or activity' },
  { value: 'other', label: 'Other', description: 'Doesn\'t fit other categories' },
] as const;

interface ReportDialogProps {
  merchant: Merchant;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ReportDialog({ merchant, open, onOpenChange }: ReportDialogProps) {
  const { user } = useCurrentUser();
  const { mutateAsync: publish, isPending } = useNostrPublish();

  const [reportType, setReportType] = useState<string>('spam');
  const [reason, setReason] = useState('');

  const handleSubmit = async () => {
    if (!user) return;

    try {
      // NIP-56: kind 1984 report
      // Must include `p` tag for the reported user
      // `e` tag referencing the event, with report type as 3rd element
      // `p` tag with report type as 3rd element
      await publish({
        kind: 1984,
        content: reason,
        tags: [
          ['e', merchant.event.id, reportType],
          ['p', merchant.pubkey, reportType],
        ],
      });

      toast({
        title: 'Report submitted',
        description: `Thank you for reporting "${merchant.name}". Your report helps keep the directory clean.`,
      });

      // Reset and close
      setReportType('spam');
      setReason('');
      onOpenChange(false);
    } catch {
      toast({
        title: 'Failed to submit report',
        description: 'Could not publish the report. Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="size-5 text-destructive" />
            Report Merchant
          </DialogTitle>
          <DialogDescription>
            Report &ldquo;{merchant.name}&rdquo; as problematic. This publishes a NIP-56 report event to Nostr relays.
          </DialogDescription>
        </DialogHeader>

        {!user ? (
          <div className="py-6 text-center space-y-4">
            <p className="text-sm text-muted-foreground">
              You need to sign in to submit a report.
            </p>
            <LoginArea className="justify-center" />
          </div>
        ) : (
          <>
            {/* Report type selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Reason</Label>
              <RadioGroup value={reportType} onValueChange={setReportType} className="space-y-2">
                {REPORT_TYPES.map(type => (
                  <label
                    key={type.value}
                    className="flex items-start gap-3 rounded-lg border p-3 cursor-pointer hover:bg-accent/50 transition-colors has-data-[state=checked]:border-primary has-data-[state=checked]:bg-primary/5"
                  >
                    <RadioGroupItem value={type.value} className="mt-0.5" />
                    <div>
                      <div className="text-sm font-medium">{type.label}</div>
                      <div className="text-xs text-muted-foreground">{type.description}</div>
                    </div>
                  </label>
                ))}
              </RadioGroup>
            </div>

            {/* Optional details */}
            <div className="space-y-2">
              <Label htmlFor="report-reason" className="text-sm font-medium">
                Details <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <Textarea
                id="report-reason"
                placeholder="Describe the issue..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="resize-none"
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleSubmit}
                disabled={isPending}
              >
                {isPending ? (
                  <>
                    <Loader2 className="size-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Report'
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
